export interface WorkdayMappingStatus {
  allMappingsApproved: boolean;
}

export interface IsEnabled {
  enabled: boolean;
}

export interface IOrganization {
  id: string;
  accountId?: string;
  parentId?: string;
  selected?: boolean;
  name?: string;
  inSelection?: boolean;
}

export interface IOrganizationItems {
  items: IOrganization[];
  totalCount: number;
}

export interface IOrganizationTree extends IOrganization {
  children: IOrganizationTree[];
  depth: number;
  parent: IOrganizationTree;
  selected: boolean;
  visible: boolean;
  expanded: boolean;
  orgColor: string;
  initials: string;
  showSelectAllChildren: boolean;
}

export interface IOrganizationTeamMapping {
  gtmhubTeamId: string;
  gtmhubTeamName?: string;
  gtmhubTeamInitials?: string;
  gtmhubTeamColor?: string;
  workdayOrganizationId?: string;
  workdayOrganizationName?: string;
  workdayOrganizationInitials?: string;
  workdayOrganizationColor?: string;
}

export interface IWorkdaySettings {
  provisioningEnabled?: boolean;
  sendInvitationEmail?: boolean;
  syncSettings?: IWorkdaySyncSettings;
  unfinishedSetupMappingId?: string;
  dataSourceSettings?: IWorkdayDataSourceSettings;
}

export interface IWorkdaySyncSettings {
  locations?: string[];
  organizationIds?: string[];
}

export interface IWorkdayDataSourceSettings {
  connectorId?: string;
  dataSourceIds?: string[];
}

export interface IGtmhubToWorkdayMapping {
  gtmhubToWorkdayMappings: Record<string, string>;
}

export interface IGtmhubUserToWorkdayWorkerMappings {
  gtmhubId: string;
  gtmhubEmail?: string;
  gtmhubName?: string;
  workdayId: string;
  workdayEmails?: string[];
  workdayName?: string;
}

export type DeltaSyncItemEntityType = "user" | "team";
export type DeltaSyncItemOperationType = "create" | "delete" | "update" | "modify_assignments";

export interface IDeltaHistorySyncs {
  items: IDeltaHistorySync[];
}

export interface IDeltaHistorySync {
  startTime: Date;
  endTime: Date;
  operations: IDeltaHistorySyncOperation[];
}

export interface IDeltaHistorySyncOperation {
  workdayEntityId: string;
  gtmhubEntityId: string;
  entityType: DeltaSyncItemEntityType;
  operationType: DeltaSyncItemOperationType;
  endTime: Date;
  success: boolean;
  skipped?: boolean;
  error?: string;
  info?: string;
  entityDetails?: IOrganizationDetails | IWorkerDetails;
}

export interface IOrganizationDetails {
  name: string;
}

export interface IWorkerDetails {
  email: string;
}

export interface IDeltaHistorySyncRecord extends IDeltaHistorySyncOperation {
  nameOrEmail?: string;
}

export interface ISync extends IDeltaHistorySync {
  dateDiff: number;
  visible: boolean;
  successStatus: "success" | "partialSuccess" | "failed";
  user: {
    create: number;
    delete: number;
    update: number;
    error: number;
  };
  team: {
    create: number;
    delete: number;
    update: number;
    error: number;
  };
}

export interface IWorkdayWorker {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  email_addresses: string[];
  id: string;
  name: string;
}

export interface IWorkdayWorkerList {
  items: IWorkdayWorker[];
}

export enum WorkdayMappingRunStatus {
  Pending = "pending",
  Started = "started",
  Ended = "ended",
  Failed = "failed",
}

export interface IOldWorkdayMappingRun {
  status: WorkdayMappingRunStatus;
  failure?: string;
  startTime?: string;
  endTime?: string;
}

export interface INewWorkdayMappingRun extends IOldWorkdayMappingRun {
  id: string;
}

export type IWorkdayMappingRun = IOldWorkdayMappingRun | INewWorkdayMappingRun;

export interface IWorkdayEntityTableMapping {
  entityName: string;
  remoteEntityName: string;
  namespace: string;
}
export interface ISelection {
  id?: string;
  name?: string;
  enabled?: boolean;
  filters?: IFilter[];
}

export interface IFilter {
  filterType: FilterType;
  entries: string[];
  selectedEntries?: string[];
  include: boolean;
}

export interface IEntries {
  items: IEntry[];
}

export interface IEntry {
  name: string;
  id: string;
  selected?: boolean;
}

export interface ConnectionHealth {
  healthy: boolean;
}

export interface CalculateSetupStepArgs {
  isConnectorHealthy: boolean;
  workdaySettings: IWorkdaySettings;
  syncInProgress: boolean;
  selections: ISelection[];
  mappingApproved: boolean;
  unfinishedSetupMapping: IWorkdayMappingRun;
}

export interface IDataAvailable {
  available: boolean;
}
export interface IDeltaRun {
  id: string;
  startedBy: string;
  status: WorkdayMappingRunStatus;
}

export interface IDeltaRunResponse {
  items: IDeltaRun[];
}

export interface DefaultQueryParams {
  skip: number;
  take: number;
}

export interface IGetOrganizationsParams extends DefaultQueryParams {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  mark_selected?: boolean;
}

export enum FilterType {
  ORGANIZATIONS = 1,
  MANAGERIAL = 2,
  LOCATIONS = 3,
}

export enum SetupStep {
  SET_UP = 1,
  DATA_SYNC = 2,
  DATA_SYNCED = 3,
  SELECTION = 4,
  MAPPING = 5,
  PROVISION = 6,
  COMPLETE = 7,
}
